'use client';

import { useCallback, type MouseEvent } from 'react';
import { useGlobalContextMenu } from '../../hooks/useGlobalContextMenu';
import { openCopilotPanel } from '../../hooks/useMentions';
import {
  findChatTabByThreadId,
  useChatTabsStore,
} from '../../store/chatTabsStore';
import { GlobalMenuItemsTypeEnum } from '../../store/globalContextMenuStore';
import { useMainContentTabs } from '../../store/layoutStore';
import type { HomeDestination } from './home-destination';
import { useOpenListFromHome } from './useOpenListFromHome';
import { useOpenTargetSettings } from './useOpenTargetSettings';

const focusCopilotThread = (threadId: string | null) => {
  const { chatTabs, setActiveChatTabId } = useChatTabsStore.getState();
  const chatTab = threadId
    ? findChatTabByThreadId({ chatTabs, threadId })
    : undefined;

  if (chatTab) {
    setActiveChatTabId(chatTab.tabId);
  }

  openCopilotPanel();
};

export function useOpenHomeDestination({
  applicationId,
}: {
  applicationId: string;
}) {
  const { navigateInActiveTab } = useMainContentTabs();
  const { openContextMenu } = useGlobalContextMenu();
  const { openList, openListContextMenu } = useOpenListFromHome({
    applicationId,
  });
  const { openSettings, openSettingsContextMenu } = useOpenTargetSettings();

  const openDestination = useCallback(
    (destination: HomeDestination) => {
      switch (destination.kind) {
        case 'list':
          return openList(destination.destination);
        case 'settings':
          return openSettings(destination.section);
        case 'tab':
          return navigateInActiveTab(destination.tab);
        case 'copilot':
          return focusCopilotThread(destination.threadId);
      }
    },
    [navigateInActiveTab, openList, openSettings],
  );

  const openDestinationContextMenu = useCallback(
    (event: MouseEvent<HTMLElement>, destination: HomeDestination) => {
      switch (destination.kind) {
        case 'list':
          return openListContextMenu(event, destination.destination);
        case 'settings':
          return openSettingsContextMenu(event, destination.section);
        case 'tab':
          return openContextMenu({
            event,
            items: [
              {
                type: GlobalMenuItemsTypeEnum.OPEN_IN_NEW_TAB,
                tab: destination.tab,
              },
            ],
          });
        case 'copilot':
          return undefined;
      }
    },
    [openContextMenu, openListContextMenu, openSettingsContextMenu],
  );

  return { openDestination, openDestinationContextMenu };
}
